import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import {withRouter} from "react-router-dom";
import {styles} from "./styles/TableStyles";
import TablePagination from "@material-ui/core/TablePagination";
import {TablePaginationActionsWrapped} from "./TablePaginationActions";
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash';
import API from './ApiInterface';

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function Row(props) {
  const { row, classes, onRestoreClick, getMeasurements } = props;
  const [open, setOpen] = React.useState(false);

  const sessionIsDeleted = row.deleted && row.deleted !== 'False';

  return (
    <React.Fragment>
      <TableRow className={classes.tabbedContent}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              setOpen(!open);
              getMeasurements(row.sessionId);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.title}
        </TableCell>
        <TableCell align="right">{row.sport}</TableCell>
        <TableCell align="right">{row.activity}</TableCell>
        <TableCell align="right">{row.startDate}</TableCell>
        <TableCell align="right">{row.updatedAt}</TableCell>
        <TableCell align="right">
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <span>{row.deleted}</span>
            {sessionIsDeleted && (
              <Box ml={1}>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  startIcon={<RestoreFromTrashIcon />}
                  onClick={() =>
                    onRestoreClick({
                      type: 'session',
                      sessionId: row.sessionId,
                      sessionTitle: row.title
                    })
                  }
                >
                  Restore
                </Button>
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse 
          in={open} timeout="auto" 
          unmountOnExit
          >
            { row.notes &&
              <Table size="small" aria-label="session notes">
                <TableBody>
                  <TableRow>
                    <TableCell>Notes: {row.notes}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            }
            <Box margin={1}>
              <Typography
                variant="h6"
                component="h6"
                className={classes.rootHeader}>
                Measurements
              </Typography>
              <Table size="small" aria-label="measurements">
                <TableHead>
                  <TableRow>
                    <TableCell align="right">Measurement</TableCell>
                    <TableCell align="right">Activity</TableCell>
                    <TableCell align="right">Player</TableCell>
                    <TableCell align="right">Device Speed Time</TableCell>
                    <TableCell align="right">Server Updated Time</TableCell>
                    <TableCell align="right">Radar</TableCell>
                    <TableCell align="right">Deleted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.measurements.length ? row.measurements.map((measurement) => {
                    const measurementIsDeleted = measurement.deleted && measurement.deleted !== 'False';
                    return (
                      <TableRow key={measurement.measurementId}>
                        <TableCell align="right">{measurement.measurement}</TableCell>
                        <TableCell align="right">{measurement.context}</TableCell>
                        <TableCell align="right">{measurement.player}</TableCell>
                        <TableCell align="right">{measurement.deviceCreatedAt}</TableCell>
                        <TableCell align="right">{measurement.updatedAt}</TableCell>
                        <TableCell align="right">{measurement.radarAddress}</TableCell>
                        <TableCell align="right">
                          <Box display="flex" justifyContent="flex-end" alignItems="center">
                            <span>{measurement.deleted}</span>
                            {measurementIsDeleted && (
                              <Box ml={1}>
                                <Button
                                  color="primary"
                                  variant="outlined"
                                  size="small"
                                  startIcon={<RestoreFromTrashIcon />}
                                  onClick={() =>
                                    onRestoreClick({
                                      type: 'measurement',
                                      sessionId: row.sessionId,
                                      measurementId: measurement.measurementId
                                    })
                                  }
                                >
                                  Restore
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }) :
                    <TableRow>
                      <TableCell/>
                      <TableCell component="th" scope="row">
                        No measurements
                      </TableCell>
                    </TableRow>
                  }
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    sessionId: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    sport: PropTypes.string.isRequired,
    activity: PropTypes.string.isRequired,
    notes: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    measurements: PropTypes.arrayOf(
      PropTypes.shape({
        measurementId: PropTypes.string.isRequired,
        measurement: PropTypes.string.isRequired,
        playerId: PropTypes.string,
        player: PropTypes.string,
        radarAddress: PropTypes.string.isRequired,
        deviceCreatedAt: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
        updatedAt: PropTypes.string.isRequired,
        deleted: PropTypes.string,
      }),
    ).isRequired,
    deleted: PropTypes.string,
  }).isRequired,
  classes: PropTypes.object.isRequired,
  onRestoreClick: PropTypes.func.isRequired,
  getMeasurements: PropTypes.func.isRequired,
};

class SessionTable extends React.Component {
  static propTypes = {
    rows: PropTypes.array.isRequired,
    paginationDetails: PropTypes.object.isRequired,
    handleChangePage: PropTypes.func.isRequired,
    handleChangeRowsPerPage: PropTypes.func.isRequired,
    getMeasurements: PropTypes.func.isRequired,

    // passed from parent
    token: PropTypes.string.isRequired,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    refreshSessions: PropTypes.func.isRequired,
  };

  state = {
    // confirm dialog
    confirmOpen: false,
    target: null, // { type: 'session'|'measurement', sessionId, measurementId?, sessionTitle? }
    busy: false,

    // snackbar
    snackOpen: false,
    snackSeverity: 'success',
    snackMessage: '',
  };

  openSnack = (severity, message) => {
    this.setState({ snackOpen: true, snackSeverity: severity, snackMessage: message });
  };

  closeSnack = () => {
    this.setState({ snackOpen: false, snackMessage: '' });
  };

  onRestoreClick = (target) => {
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    this.setState({ confirmOpen: true, target });
  };

  handleDialogClose = (event, reason) => {
    if (this.state.busy && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    this.setState({ confirmOpen: false, target: null });
  };

  handleConfirmRestore = async () => {
    const { target } = this.state;
    const { token, userId, refreshSessions, getMeasurements } = this.props;
    if (!target) return;

    this.setState({ busy: true });

    try {
      let res;
      if (target.type === 'session') {
        res = await API.restoreSession(token, userId, target.sessionId);
      } else {
        res = await API.restoreMeasurement(token, userId, target.measurementId);
      }

      const ok = res && typeof res.ok === 'boolean' ? res.ok : !(res instanceof Error);

      if (!ok) {
        let msg = 'Restore failed';
        try {
          if (res && res.text) {
            const t = await res.text();
            if (t) msg = t;
          }
        } catch (err) {
          // ignore
        }
        this.openSnack('error', msg);
      } else {
        if (target.type === 'session') {
            await refreshSessions();
            await getMeasurements(target.sessionId, true);   // force reload after session restore
        } else {
            await getMeasurements(target.sessionId, true);   // force reload after measurement restore
        }
        this.openSnack('success', 'Restored successfully.');
      }
    } catch (e) {
      this.openSnack('error', e?.message || 'Restore failed.');
    } finally {
      this.setState({ busy: false, confirmOpen: false, target: null });
    }
  };

  render() {
    const { rows, classes, handleChangePage, handleChangeRowsPerPage, paginationDetails } = this.props;
    const { confirmOpen, target, busy, snackOpen, snackSeverity, snackMessage } = this.state;

    const dialogTitle =
      target?.type === 'session'
        ? 'Restore session?'
        : target?.type === 'measurement'
        ? 'Restore measurement?'
        : 'Confirm';

    const dialogText =
      target?.type === 'session'
        ? `Are you sure you want to restore this session${target?.sessionTitle ? `: "${target.sessionTitle}"` : ''}?`
        : target?.type === 'measurement'
        ? 'Are you sure you want to restore this measurement?'
        : '';

    return (
      <div>
        <Typography
          variant="h6"
          component="h6"
          className={classes.rootHeader}>
          Sessions
        </Typography>
        <Paper className={classes.tabbedContent}>
          <div>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell/>
                  <TableCell>Title</TableCell>
                  <TableCell align="right">Sport</TableCell>
                  <TableCell align="right">Activity</TableCell>
                  <TableCell align="right">Session Start Time</TableCell>
                  <TableCell align="right">Server Updated Time</TableCell>
                  <TableCell align="right">Deleted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                { rows.length ?
                  rows.map((row) => (
                    <Row
                      key={row.sessionId}
                      row={row}
                      classes={classes}
                      getMeasurements={this.props.getMeasurements}
                      onRestoreClick={this.onRestoreClick}
                    />
                  )) :
                  <TableRow>
                    <TableCell/>
                    <TableCell component="th" scope="row">
                      No sessions
                    </TableCell>
                    <TableCell/>
                    <TableCell/>
                    <TableCell/>
                    <TableCell/>
                    <TableCell/>
                  </TableRow>
                }
              </TableBody>
              <TableFooter>
                <TableRow className={classes.row}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    colSpan={7}
                    count={paginationDetails.count}
                    rowsPerPage={paginationDetails.rowsPerPage}
                    page={paginationDetails.page}
                    SelectProps={{
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActionsWrapped}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </Paper>

        <Dialog
          open={confirmOpen}
          onClose={this.handleDialogClose}
          aria-labelledby="confirm-restore-title"
          disableEscapeKeyDown={busy}
        >
          <DialogTitle id="confirm-restore-title">{dialogTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {dialogText}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleDialogClose} color="default" disabled={busy}>
              Cancel
            </Button>
            <Button onClick={this.handleConfirmRestore} color="primary" variant="contained" disabled={busy}>
              {busy ? 'Restoring…' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackOpen}
          autoHideDuration={3500}
          onClose={this.closeSnack}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={this.closeSnack} severity={snackSeverity}>
            {snackMessage}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

export default withRouter(withStyles(styles)(SessionTable));
